import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from 'context/PermissionsContext';
import { menuItems } from './constants';
import logo from '../../assets/images/grupo-defensa-logo.svg';
import logoD from '../../assets/images/grupo-d-logo.svg';
import './mainLayout.css';
import UserMenu from '../../components/UserMenu/UserMenu.jsx';
import { getCurrentUser } from '../../services/authService';
import { getUserById } from '../../services/myDataService';
import { configurationRoleBlock } from './configurationBlock';
import { DesignSystemContext } from '../../context/DesignSystemContext';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector.jsx';
import Notifications from 'components/Notifications/Notifications';
import { useMenuPermissions } from 'permissions/hooks/useMenuPermissions';
import { useLanguage } from '../../context/LanguageContext';
import { SHORT_TO_FULL_LANGUAGE, toShortLanguage } from '../../i18nConfig';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { mainLayoutI18n } from './i18n';
import { useCurrency } from 'context/CurrencyContext';

const resolveEnvironment = () => {
  if (typeof window === 'undefined') return null;
  const { hostname, port } = window.location;
  if (!hostname) return null;

  if (hostname === '172.16.11.40' && port === '93') return 'dev';
  if (hostname === '172.16.11.29') return 'qa';
  if (hostname === '172.16.10.45' && (port === '81' || port === '')) {
    return 'prod';
  }

  return null;
};

const MainLayout = ({ DesignSystemComponents }) => {
  const { MenuItem, SvgImage, IconButton, SvgIcon, Button } =
    DesignSystemComponents ?? {};

  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const mainLayoutLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: mainLayoutI18n });
  const { logout, initializing, inactivitySecondsLeft } = useAuth();
  const { permissionsLoading } = usePermissions();
  const { currency } = useCurrency();

  const authUser = getCurrentUser();
  const environment = resolveEnvironment();
  const environmentLabel =
    environment && environment !== 'prod'
      ? t('main_layout_env_warning_title')
      : null;
  const authUserId =
    authUser?.id ?? authUser?.userId ?? authUser?.usuarioAsignadoId ?? null;
  const isDevAccount = configurationRoleBlock?.developersUsers?.allowedUserIds
    ? configurationRoleBlock.developersUsers.allowedUserIds.includes(authUserId)
    : false;
  const shouldShowEnvironmentWarning =
    environment && environment !== 'prod' && !isDevAccount;
  const PROD_URL = 'http://172.16.10.45:81/';
  const handleGoToProduction = () => {
    window.open(PROD_URL, '_blank', 'noopener,noreferrer');
  };

  const { canSeeMenuItem, canSeeConfiguration } = useMenuPermissions();

  const [fullUser, setFullUser] = useState(null);

  const [open, setOpen] = useState(false);
  const [collapseMenu, setCollapseMenu] = useState(false);

  useEffect(() => {
    mainLayoutI18n.changeLanguage(mainLayoutLanguage);
  }, [mainLayoutLanguage]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (authUser?.userId) {
      getUserById(authUser.userId)
        .then((data) => setFullUser(data))
        .catch((err) => console.error('Error fetching user data:', err));
    }
  }, [authUser?.userId]);

  const handleLogout = async () => {
    try {
      localStorage.removeItem('task_comment_history');

      await logout();
    } finally {
      navigate('/login');
    }
  };

  const topEntries = menuItems.filter((el) => el.linkKey !== 'menu_logout');

  const renderItem = (item, index, parentId = '') => {
    if (!canSeeMenuItem(item)) return null;

    const id = parentId ? `${parentId}-${index}` : `${index}`;
    const isActive = location.pathname === item.linkTo;
    const hasActiveChild =
      item.children?.some(
        (child) =>
          location.pathname === child.linkTo ||
          child.children?.some((grand) => location.pathname === grand.linkTo)
      ) || false;

    const text = t(item.linkKey || '');

    if (item.children?.length) {
      return (
        <MenuItem
          key={`menu-${id}`}
          icon={item.icon}
          linkText={text}
          itemId={id}
          className={`mb-1 ps-3 pe-1 collapsed-menu-item ${isActive || hasActiveChild ? 'selected' : ''}`}
        >
          <div>
            {item.children
              ?.filter(canSeeMenuItem)
              .map((child, idx) => renderItem(child, idx, id))}
          </div>
        </MenuItem>
      );
    }

    const className =
      parentId !== ''
        ? isActive
          ? 'selected-submenu'
          : ''
        : isActive
          ? 'selected'
          : '';

    return (
      <Link
        key={`lnk-${id}`}
        to={item.linkTo || '#'}
        className='w-100 d-block ps-3 pe-1'
        aria-current={isActive ? 'page' : undefined}
      >
        <MenuItem
          className={`mb-1 collapsed-menu-item ${className}`}
          icon={item.icon}
          linkText={text}
          disabled={item.disabled}
        />
      </Link>
    );
  };

  if (initializing || permissionsLoading) {
    return null;
  }

  const selectedLanguage = toShortLanguage(language);
  const handleLanguageChange = (code) => {
    setLanguage(SHORT_TO_FULL_LANGUAGE[code] || code);
  };

  return (
    <div
      className='layout-container'
      style={{
        gridTemplateColumns: collapseMenu ? '4.9375rem 1fr' : '17.9375rem 1fr',
      }}
    >
      <header className='topbar'>
        <Link
          to='/'
          className='mobile-page-logo cursor-pointer'
          aria-label={t('main_layout_go_home_aria')}
        >
          <SvgImage
            src={collapseMenu ? logoD : logo}
            className='mobile-page-logo'
          />
        </Link>
        <div className='topbar-right'>
          <LanguageSelector
            DesignSystemComponents={DesignSystemComponents}
            value={selectedLanguage}
            onChange={handleLanguageChange}
          />

          {/* {currencyObj && (
            <span className='currency-badge caption'>
              {currencyObj.code} {currencyObj.symbol}
            </span>
          )} */}

          {canSeeConfiguration && (
            <Link
              to='/configuracion'
              className='header-icon-btn'
              aria-label={t('main_layout_go_config_aria')}
            >
              <div className='d-flex menu-link align-items-center gap-2'>
                <SvgIcon
                  ariaLabel={t('main_layout_go_config_aria')}
                  name='gear'
                  size='16px'
                  stroke='var(--dark)'
                />
                <p className='caption'>{t('main_layout_config_label')}</p>
              </div>
            </Link>
          )}
          <Notifications DesignSystemComponents={DesignSystemComponents} />
          <UserMenu
            user={{
              name: fullUser?.name?.split(' ')[0] || authUser?.email,
              fullName: fullUser?.name || authUser?.email,
              email: fullUser?.email || authUser?.email || '',
              profileImageUrl: fullUser?.profileImageUrl,
              role: authUser?.role,
            }}
            SvgIcon={SvgIcon}
            onProfileClick={() => navigate('/mi-perfil')}
            onLogout={handleLogout}
          />
          <IconButton
            ariaLabel={
              open
                ? t('main_layout_close_menu_aria')
                : t('main_layout_open_menu_aria')
            }
            icon={open ? 'close' : 'menu'}
            onClick={() => setOpen((prev) => !prev)}
            size='lg'
          />
        </div>
      </header>
      <aside
        className={`sidebar py-3 ${open ? 'sidebar-open' : ''} ${collapseMenu ? 'collapsed-menu' : ''}`}
      >
        <div className='px-4'>
          <div className='mt-3 desktop-logo'>
            <Link
              to='/'
              className='cursor-pointer'
              aria-label={t('main_layout_go_home_aria')}
            >
              <SvgImage
                src={collapseMenu ? logoD : logo}
                className='cursor-pointer'
              />
            </Link>
          </div>
          <hr className='line-mobile-hide' />
        </div>
        <div className='sidebar-content'>
          <nav
            aria-label={t('main_layout_main_menu_aria')}
            className='flex-grow-1 sidebar-nav-wrapper'
          >
            <div className='sidebar-nav-scroll'>
              {topEntries.map((el, idx) => renderItem(el, idx))}
            </div>
            <div
              className={`d-flex pt-3 ${collapseMenu ? 'justify-content-center' : 'justify-content-end me-4'}`}
            >
              <IconButton
                ariaLabel={t('main_layout_collapse_menu_aria')}
                icon={collapseMenu ? 'chevronDoubleRight' : 'chevronDoubleLeft'}
                onClick={() => setCollapseMenu((prev) => !prev)}
                size='lg'
              />
            </div>
          </nav>
        </div>
      </aside>
      <main className='content'>
        <div className='header-desktop'>
          <LanguageSelector
            DesignSystemComponents={DesignSystemComponents}
            value={selectedLanguage}
            onChange={handleLanguageChange}
          />
          {currency && (
            <span className='currency-badge caption'>{currency}</span>
          )}
          {canSeeConfiguration && (
            <Link
              to='/configuracion'
              className='header-icon-btn'
              aria-label={t('main_layout_go_config_aria')}
            >
              <div className='d-flex menu-link align-items-center gap-2'>
                <SvgIcon
                  ariaLabel={t('main_layout_go_config_aria')}
                  name='gear'
                  size='16px'
                  stroke='var(--dark)'
                />
                <p className='caption'>{t('main_layout_config_label')}</p>
              </div>
            </Link>
          )}
          <Notifications DesignSystemComponents={DesignSystemComponents} />
          <UserMenu
            user={{
              name:
                fullUser?.name?.split(' ').slice(0, 2).join(' ') ||
                authUser?.email,
              fullName: fullUser?.name || authUser?.email,
              position:
                fullUser?.position || t('main_layout_user_position_fallback'),
              email: fullUser?.email || authUser?.email || '',
              profileImageUrl: fullUser?.profileImageUrl,
              role: authUser?.role,
            }}
            SvgIcon={SvgIcon}
            onProfileClick={() => navigate('/mi-perfil')}
            onLogout={handleLogout}
          />
        </div>
        <div className='content-inner container-fluid'>
          <div className='row'>
            <div className='col-12'>
              <div className='main-padding'>
                <DesignSystemContext.Provider value={DesignSystemComponents}>
                  {shouldShowEnvironmentWarning && (
                    <div className='env-warning'>
                      <div className='d-flex gap-2 align-items-center mb-3'>
                        <SvgIcon
                          className='priority-icon'
                          name='exclamationTriangle'
                          size='1.5rem'
                          fill='var(--error-500)'
                        />
                        <h3 className='fw-semibold'>{environmentLabel}</h3>
                      </div>

                      <p className='caption'>
                        {t('main_layout_env_warning_text_1')}
                      </p>
                      <p className='caption'>
                        {t('main_layout_env_warning_text_2')}
                      </p>
                      <p className=' caption fw-semibold mt-3'>
                        {t('main_layout_env_warning_text_3')}
                      </p>
                      <div className='mt-2'>
                        {Button ? (
                          <Button
                            label={t('main_layout_go_official_version')}
                            iconEnd='arrowRightShort'
                            variant='primary'
                            onClick={handleGoToProduction}
                            size='sm'
                          />
                        ) : (
                          <button
                            type='button'
                            className='btn btn-primary btn-sm'
                            onClick={handleGoToProduction}
                          >
                            {t('main_layout_go_official_version')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {inactivitySecondsLeft !== null && (
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: 'var(--warning-500, #ffc107)',
                        color: '#000',
                        padding: '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      <SvgIcon
                        name='exclamationTriangle'
                        size='1.25rem'
                        fill='#000'
                      />
                      <span className='caption fw-semibold'>
                        {t('main_layout_inactivity_warning')}
                      </span>
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          minWidth: '3ch',
                        }}
                      >
                        {String(Math.floor(inactivitySecondsLeft / 60)).padStart(2, '0')}:
                        {String(inactivitySecondsLeft % 60).padStart(2, '0')}
                      </span>
                    </div>
                  )}
                  <Outlet context={{ DesignSystemComponents }} />
                </DesignSystemContext.Provider>
              </div>
            </div>
          </div>
        </div>
      </main>
      {open && <div className='backdrop' onClick={() => setOpen(false)} />}
    </div>
  );
};

export default MainLayout;
